import { useRef, useEffect, useCallback } from 'react';

import Countdown, { CountdownRenderProps } from 'react-countdown';
import classNames from 'classnames';

import { pluralize } from '../../format';

import useStyles from './RedoCountdown.styles';

/**
 * Свойства компонента {@link RedoCountdown}
 */
interface RedoCountdownProps {
  /**
   * Текст метки, отображаемый перед отсчётом.
   */
  label: string;

  /**
   * Флаг, указывающий, отключён ли отсчёт.
   */
  disabled: boolean;

  /**
   * Временная метка в миллисекундах, до которой должен работать отсчёт.
   */
  finalDate: number;

  /**
   * Функция для переключения видимости таймера.
   */
  toggleTimerVisibility: () => void;
}

/**
 * Компонент отсчёта времени до указанной конечной даты.
 * Использует библиотеку `react-countdown` для управления отсчётом и отображает метку с оставшимся временем.
 *
 * @internal
 * @param label - Текст метки, отображаемый перед отсчётом.
 * @param disabled - Флаг, указывающий, отключён ли отсчёт.
 * @param finalDate - Временная метка в миллисекундах, до которой должен работать отсчёт.
 * @param toggleTimerVisibility - Функция для переключения видимости таймера.
 * @returns Компонент RedoCountdown.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link RedoCountdownProps}
 */
export const RedoCountdown = ({
  label,
  disabled,
  finalDate,
  toggleTimerVisibility,
}: RedoCountdownProps) => {
  const { classes } = useStyles();

  const counterRef = useRef<Countdown | null>(null);

  const pluralizeSeconds = useCallback((count: number) => {
    return pluralize(count, ['секунда', 'секунды', 'секунд'], {
      withZeroPad: true,
    });
  }, []);

  useEffect(() => {
    const isStarted = !!counterRef.current?.isStarted();

    let timer: ReturnType<typeof setTimeout>;
    if (!isStarted && !disabled) {
      timer = setTimeout(() => {
        counterRef.current?.start();
      });
    }

    if (isStarted && disabled) {
      counterRef.current?.pause();
    }

    return () => {
      clearTimeout(timer);
    };
  }, [disabled]);

  return (
    <>
      <div className={classes.label}>{label} через:</div>
      <Countdown
        key={finalDate}
        date={finalDate}
        autoStart={false}
        onComplete={toggleTimerVisibility}
        ref={counterRef}
        renderer={({ seconds }: CountdownRenderProps) => (
          <div
            className={classNames(
              classes.countdown,
              disabled && classes.countdownDisabled,
              'aqa_redo_countdown',
            )}
          >
            {pluralizeSeconds(seconds)}
          </div>
        )}
      />
    </>
  );
};
